import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { CAMPANHAS, iniciais, avColor, dataRelativa } from '../services/utils';

const STATUS_CRM = {
  novo:          { label: 'Novo',           bg: '#eff6ff', text: '#1e3a8a', cor: '#3b82f6' },
  qualificado:   { label: 'Qualificado',    bg: '#f0fdf4', text: '#14532d', cor: '#22c55e' },
  em_negociacao: { label: 'Em negociação',  bg: '#fefce8', text: '#713f12', cor: '#eab308' },
  contratado:    { label: 'Contratado',     bg: '#f0fdf4', text: '#14532d', cor: '#16a34a' },
  perdido:       { label: 'Perdido',        bg: '#fef2f2', text: '#7f1d1d', cor: '#ef4444' },
};

const COLUNAS = ['novo','qualificado','em_negociacao','contratado','perdido'];

export default function CRM() {
  const [view,         setView]         = useState('kanban');
  const [kanban,       setKanban]       = useState({});
  const [leads,        setLeads]        = useState([]);
  const [busca,        setBusca]        = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [leadAberto,   setLeadAberto]   = useState(null);
  const [showNovo,     setShowNovo]     = useState(false);

  const carregarKanban = useCallback(async () => {
    try {
      const { data } = await api.get('/crm/kanban');
      setKanban(data);
    } catch {}
  }, []);

  const carregarLeads = useCallback(async () => {
    try {
      const params = {};
      if (busca)        params.busca  = busca;
      if (filtroStatus) params.status = filtroStatus;
      const { data } = await api.get('/crm/leads', { params });
      setLeads(data.leads);
    } catch {}
  }, [busca, filtroStatus]);

  useEffect(() => {
    if (view === 'kanban') carregarKanban();
    else carregarLeads();
  }, [view, carregarKanban, carregarLeads]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}><i className="ti ti-address-book" style={{ fontSize: 18 }} /> CRM — Leads</div>
          <div style={s.sub}>Gerencie seus leads e oportunidades</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...s.btn, ...(view === 'kanban' ? s.btnActive : {}) }} onClick={() => setView('kanban')}>
            <i className="ti ti-layout-kanban" /> Kanban
          </button>
          <button style={{ ...s.btn, ...(view === 'lista' ? s.btnActive : {}) }} onClick={() => setView('lista')}>
            <i className="ti ti-list" /> Lista
          </button>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => setShowNovo(true)}>
            <i className="ti ti-plus" /> Novo Lead
          </button>
        </div>
      </div>

      {view === 'kanban' && (
        <div style={s.kanban}>
          {COLUNAS.map(col => {
            const st    = STATUS_CRM[col];
            const items = kanban[col] || [];
            return (
              <div key={col} style={s.coluna}>
                <div style={s.colunaHeader}>
                  <span style={{ ...s.colunaDot, background: st.cor }} />
                  <span style={s.colunaTitle}>{st.label}</span>
                  <span style={s.colunaCount}>{items.length}</span>
                </div>
                <div style={s.colunaBody}>
                  {items.map((lead, i) => (
                    <KanbanCard key={lead.id} lead={lead} index={i} onClick={() => setLeadAberto(lead.id)} />
                  ))}
                  {items.length === 0 && <div style={s.colunaEmpty}>Nenhum lead</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'lista' && (
        <div style={s.listaWrap}>
          <div style={s.filtros}>
            <div style={{ position: 'relative', flex: 1 }}>
              <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }} />
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, CPF ou telefone..." style={{ ...s.input, paddingLeft: 32 }} />
            </div>
            <select style={s.input} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
              <option value="">Todos os status</option>
              {COLUNAS.map(c => <option key={c} value={c}>{STATUS_CRM[c].label}</option>)}
            </select>
          </div>
          <div style={s.tabela}>
            <div style={s.tabelaHeader}>
              <span style={{ flex: 2 }}>Nome</span>
              <span style={{ flex: 1 }}>Telefone</span>
              <span style={{ flex: 1 }}>Campanha</span>
              <span style={{ flex: 1 }}>Status</span>
              <span style={{ flex: 1 }}>Atualizado</span>
            </div>
            {leads.map(lead => {
              const st   = STATUS_CRM[lead.status] || STATUS_CRM.novo;
              const camp = CAMPANHAS[lead.campanha] || {};
              const av   = avColor(lead.id);
              return (
                <div key={lead.id} style={s.tabelaRow} onClick={() => setLeadAberto(lead.id)}>
                  <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="avatar" style={{ width: 32, height: 32, fontSize: 11, background: av.bg, color: av.color }}>
                      {iniciais(lead.nome || lead.telefone)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{lead.nome || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{lead.cpf || ''}</div>
                    </div>
                  </div>
                  <span style={{ flex: 1, fontSize: 12 }}>{lead.telefone || lead.tel_contato || '—'}</span>
                  <span style={{ flex: 1 }}>
                    {lead.campanha ? <span className="pill" style={{ background: camp.bg, color: camp.text, fontSize: 10 }}>{camp.label || lead.campanha}</span> : '—'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <span className="pill" style={{ background: st.bg, color: st.text, fontSize: 10 }}>{st.label}</span>
                  </span>
                  <span style={{ flex: 1, fontSize: 11, color: 'var(--text-muted)' }}>{dataRelativa(lead.atualizado_em)}</span>
                </div>
              );
            })}
            {leads.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum lead encontrado</div>
            )}
          </div>
        </div>
      )}

      {leadAberto && (
        <ModalLead leadId={leadAberto} onClose={() => { setLeadAberto(null); carregarKanban(); carregarLeads(); }} />
      )}
      {showNovo && (
        <ModalNovoLead onClose={() => { setShowNovo(false); carregarKanban(); carregarLeads(); }} />
      )}
    </div>
  );
}

function KanbanCard({ lead, onClick, index }) {
  const av   = avColor(index);
  const camp = CAMPANHAS[lead.campanha] || {};
  return (
    <div style={s.card} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div className="avatar" style={{ width: 30, height: 30, fontSize: 10, background: av.bg, color: av.color }}>
          {iniciais(lead.nome || lead.telefone)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.nome || 'Sem nome'}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{lead.telefone || '—'}</div>
        </div>
      </div>
      {lead.campanha && (
        <span className="pill" style={{ background: camp.bg, color: camp.text, fontSize: 10, marginBottom: 6 }}>{camp.label || lead.campanha}</span>
      )}
      {lead.atendente_nome && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="ti ti-user" style={{ fontSize: 10 }} /> {lead.atendente_nome}
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{dataRelativa(lead.atualizado_em)}</div>
    </div>
  );
}

function ModalLead({ leadId, onClose }) {
  const [lead,      setLead]      = useState(null);
  const [historico, setHistorico] = useState([]);
  const [conversas, setConversas] = useState([]);
  const [editando,  setEditando]  = useState(false);
  const [obs,       setObs]       = useState('');
  const [salvando,  setSalvando]  = useState(false);
  const [form,      setForm]      = useState({});

  useEffect(() => { carregar(); }, [leadId]);

  async function carregar() {
    try {
      const { data } = await api.get('/crm/leads/' + leadId);
      setLead(data.lead);
      setForm(data.lead);
      setHistorico(data.historico);
      setConversas(data.conversas);
    } catch {}
  }

  async function salvar() {
    setSalvando(true);
    try {
      await api.put('/crm/leads/' + leadId, form);
      setEditando(false);
      carregar();
    } catch {}
    setSalvando(false);
  }

  async function addObs() {
    if (!obs.trim()) return;
    try {
      await api.post('/crm/leads/' + leadId + '/observacao', { descricao: obs });
      setObs('');
      carregar();
    } catch {}
  }

  async function excluir() {
    if (!window.confirm('Excluir este lead permanentemente?')) return;
    await api.delete('/crm/leads/' + leadId);
    onClose();
  }

  if (!lead) return (
    <div style={s.modalBg}>
      <div style={s.modal}>
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      </div>
    </div>
  );

  const st = STATUS_CRM[lead.status] || STATUS_CRM.novo;

  return (
    <div style={s.modalBg} onClick={onClose}>
      <div style={{ ...s.modal, width: 680, maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, background: avColor(lead.id).bg, color: avColor(lead.id).color }}>
              {iniciais(lead.nome || lead.telefone)}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{lead.nome || 'Lead sem nome'}</div>
              <span className="pill" style={{ background: st.bg, color: st.text, fontSize: 10 }}>{st.label}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={s.btn} onClick={() => setEditando(!editando)}>
              <i className={`ti ${editando ? 'ti-x' : 'ti-edit'}`} /> {editando ? 'Cancelar' : 'Editar'}
            </button>
            <button style={{ ...s.btn, borderColor: '#fca5a5', color: '#dc2626' }} onClick={excluir}>
              <i className="ti ti-trash" /> Excluir
            </button>
            <button style={s.modalClose} onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, maxHeight: '75vh', overflowY: 'auto' }}>
          <div style={{ padding: 20, borderRight: '1px solid var(--border)' }}>
            <div style={s.secTitle}>Dados do lead</div>
            {editando ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[['nome','Nome'],['cpf','CPF'],['email','Email'],['telefone','Telefone']].map(([k,l]) => (
                  <div key={k}>
                    <label style={s.label}>{l}</label>
                    <input style={s.input} value={form[k]||''} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
                  </div>
                ))}
                <div>
                  <label style={s.label}>Status</label>
                  <select style={s.input} value={form.status||'novo'} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                    {COLUNAS.map(c => <option key={c} value={c}>{STATUS_CRM[c].label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Observações</label>
                  <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={form.observacoes||''} onChange={e => setForm(p => ({...p,observacoes:e.target.value}))} />
                </div>
                {[['campo1','Campo 1'],['campo2','Campo 2'],['campo3','Campo 3']].map(([k,l]) => (
                  <div key={k}>
                    <label style={s.label}>{l}</label>
                    <input style={s.input} value={form[k]||''} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
                  </div>
                ))}
                <button style={{ ...s.btn, ...s.btnPrimary }} onClick={salvar} disabled={salvando}>
                  {salvando ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[['Nome',lead.nome],['CPF',lead.cpf],['Email',lead.email],['Telefone',lead.telefone],['Campanha',lead.campanha],['Obs.',lead.observacoes],['Campo 1',lead.campo1],['Campo 2',lead.campo2],['Campo 3',lead.campo3]].map(([l,v]) => v ? (
                  <div key={l} style={s.infoRow}>
                    <span style={s.infoLabel}>{l}</span>
                    <span style={s.infoVal}>{v}</span>
                  </div>
                ) : null)}
              </div>
            )}
            {conversas.length > 0 && (
              <>
                <div style={{ ...s.secTitle, marginTop: 16 }}>Conversas recentes</div>
                {conversas.slice(0,3).map(c => (
                  <div key={c.id} style={s.convRow}>
                    <i className="ti ti-message-circle-2" style={{ fontSize: 13, color: 'var(--green)' }} />
                    <span style={{ fontSize: 12 }}>{STATUS_CRM[c.status]?.label || c.status} · {dataRelativa(c.aberta_em)}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <div style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
            <div style={s.secTitle}>Histórico</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={obs} onChange={e => setObs(e.target.value)} placeholder="Adicionar observação..." style={{ ...s.input, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addObs()} />
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={addObs}>
                <i className="ti ti-send" />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {historico.map(h => (
                <div key={h.id} style={s.histItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
                      <i className={`ti ${h.tipo === 'status' ? 'ti-refresh' : 'ti-message'}`} style={{ fontSize: 10, marginRight: 4 }} />
                      {h.usuario_nome || 'Sistema'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{dataRelativa(h.criado_em)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{h.descricao}</div>
                </div>
              ))}
              {historico.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Nenhum histórico ainda</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalNovoLead({ onClose }) {
  const [form,     setForm]     = useState({ status: 'novo', campanha: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro,     setErro]     = useState('');

  async function salvar() {
    setSalvando(true);
    setErro('');
    try {
      await api.post('/crm/leads', { ...form, contato_id: 1 });
      onClose();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao criar lead.');
    }
    setSalvando(false);
  }

  return (
    <div style={s.modalBg} onClick={onClose}>
      <div style={{ ...s.modal, width: 420 }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHead}>
          <span style={{ fontWeight: 600 }}>Novo Lead</span>
          <button style={s.modalClose} onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['nome','Nome completo'],['cpf','CPF'],['email','Email'],['telefone','Telefone']].map(([k,l]) => (
            <div key={k}>
              <label style={s.label}>{l}</label>
              <input style={s.input} value={form[k]||''} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
            </div>
          ))}
          <div>
            <label style={s.label}>Campanha</label>
            <select style={s.input} value={form.campanha} onChange={e => setForm(p => ({...p,campanha:e.target.value}))}>
              <option value="">Selecione...</option>
              <option value="doenca">Auxílio Doença</option>
              <option value="acid">Aux. Acidente</option>
              <option value="vinc">Vínculo Empregatício</option>
              <option value="bpc">BPC Loas</option>
              <option value="mat">Salário Maternidade</option>
              <option value="com">Comercial</option>
            </select>
          </div>
          <div>
            <label style={s.label}>Status inicial</label>
            <select style={s.input} value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
              {COLUNAS.map(c => <option key={c} value={c}>{STATUS_CRM[c].label}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Observações</label>
            <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={form.observacoes||''} onChange={e => setForm(p => ({...p,observacoes:e.target.value}))} />
          </div>
          {erro && <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>{erro}</div>}
          <button style={{ ...s.btn, ...s.btnPrimary, justifyContent: 'center' }} onClick={salvar} disabled={salvando}>
            {salvando ? 'Criando...' : 'Criar lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:    { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-app)' },
  header:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 24px 16px', flexShrink: 0 },
  title:   { fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  sub:     { fontSize: 13, color: 'var(--text-muted)' },
  btn:     { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer' },
  btnActive:  { background: '#1a1f2e', borderColor: '#1a1f2e', color: '#fff' },
  btnPrimary: { background: 'var(--green)', borderColor: 'var(--green)', color: '#fff' },
  kanban:  { display: 'flex', gap: 12, padding: '0 24px 24px', overflowX: 'auto', flex: 1 },
  coluna:  { minWidth: 220, maxWidth: 260, flex: '0 0 240px', display: 'flex', flexDirection: 'column', background: '#f8f8fb', borderRadius: 12, overflow: 'hidden' },
  colunaHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: '#fff', borderBottom: '1px solid var(--border)' },
  colunaDot:    { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  colunaTitle:  { flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' },
  colunaCount:  { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--border)', padding: '1px 7px', borderRadius: 99 },
  colunaBody:   { flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  colunaEmpty:  { fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 },
  card:    { background: '#fff', borderRadius: 10, padding: 12, cursor: 'pointer', border: '1px solid var(--border)', transition: 'box-shadow 0.15s' },
  listaWrap: { flex: 1, overflowY: 'auto', padding: '0 24px 24px' },
  filtros:   { display: 'flex', gap: 8, marginBottom: 12 },
  tabela:    { background: '#fff', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' },
  tabelaHeader: { display: 'flex', padding: '10px 16px', background: '#f8f8fb', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' },
  tabelaRow:    { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' },
  input:   { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', fontSize: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' },
  label:   { display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal:   { background: '#fff', borderRadius: 18, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' },
  modalHead: { padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
  modalClose: { border: 'none', background: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' },
  secTitle:   { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  infoRow:    { display: 'flex', justifyContent: 'space-between', fontSize: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' },
  infoLabel:  { color: 'var(--text-muted)', fontWeight: 500 },
  infoVal:    { color: 'var(--text-primary)', maxWidth: '60%', textAlign: 'right' },
  convRow:    { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 },
  histItem:   { background: 'var(--bg-app)', borderRadius: 8, padding: '8px 10px' },
};